import * as React from 'react';

import moment from 'moment';

import {Card} from 'shells/card/card';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Stack} from 'shells/layout/stack/stack';
import {Link} from 'shells/link/link';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {Body} from 'shells/typography/body';
import {Title} from 'shells/typography/title';

import type {CampaignType} from 'nutshell-graphql-types';
import type {EditionFragment as Edition} from 'nutshell-graphql-types';

import {CollapsibleEmailStats} from '../../audiences/membership/collapsible-email-stats';

import type {SelectedRecipients} from './new-recipients-selection-view';

import styles from './new-preview-data.module.css';

export const MIN_AUDIENCE_SIZE = 20;
export const RECOMMENDED_AUDIENCE_SIZE = 1000;

type Props = {
    edition: Edition;
    scheduledDate: moment.Moment | null | undefined;
    type: CampaignType;
    isScheduling: boolean;
    selectedRecipients: SelectedRecipients;
};

export function NewPreviewData(props: Props) {
    const getUtmCampaignParameters = () => {
        if (props.edition.utmParameters.campaign && props.edition.utmParameters.campaign.length) {
            return <div>utm_campaign = {props.edition.utmParameters.campaign}</div>;
        }

        return <div>utm_campaign = {props.edition.defaultUtmParameters.campaign}</div>;
    };

    const audienceCount =
        props.selectedRecipients &&
        props.selectedRecipients.recipientSelection &&
        typeof props.selectedRecipients.recipientSelection.recipientCount === 'number'
            ? props.selectedRecipients.recipientSelection.recipientCount
            : undefined;

    let banner;
    if (props.edition.isABTesting) {
        if (audienceCount && audienceCount < MIN_AUDIENCE_SIZE) {
            banner = (
                <FlashBanner type='error' hasBorder={true}>
                    Your audience is too small to send. {MIN_AUDIENCE_SIZE} or more recipients are
                    required for A/B tests.
                </FlashBanner>
            );
        } else if (audienceCount && audienceCount < RECOMMENDED_AUDIENCE_SIZE) {
            banner = (
                <FlashBanner type='warning' hasBorder={true}>
                    For better test results, consider sending to an audience of over{' '}
                    {RECOMMENDED_AUDIENCE_SIZE} people.
                </FlashBanner>
            );
        }
    }

    return (
        <Stack spacing={16}>
            {props.type === 'NEWSLETTER' ? (
                <>
                    {renderPreviewDetailItem('Newsletter', props.edition.campaign.name)}
                    {renderPreviewDetailItem('Edition', props.edition.name)}
                </>
            ) : (
                renderPreviewDetailItem('Broadcast', props.edition.name)
            )}
            {props.isScheduling && props.scheduledDate
                ? renderPreviewDetailItem(
                      'Sending on',
                      <FormattedTimestamp
                          datetime={props.scheduledDate.unix()}
                          format={FormattedTimestamp.formats.longDateWithTime}
                          addTimezoneAbbreviation={true}
                      />
                  )
                : undefined}
            {renderPreviewDetailItem(
                'From',
                props.edition.from ? props.edition.from.emailAddress.formatted : null
            )}
            {renderToPreviewDetailItem(props.selectedRecipients)}
            {banner}
            {!props.edition.isABTesting &&
                renderPreviewDetailItem(
                    'Subject',
                    props.edition.emailTemplate && props.edition.emailTemplate.subject.length
                        ? props.edition.emailTemplate.subject
                        : null
                )}
            {!props.edition.isABTesting &&
                renderPreviewDetailItem(
                    'Preview text',
                    props.edition.emailTemplate && props.edition.emailTemplate.previewText.length
                        ? props.edition.emailTemplate.previewText
                        : 'None'
                )}
            {props.edition.useUtmParameters
                ? renderPreviewDetailItem(
                      'UTM parameters',
                      <div>
                          {props.edition.utmParameters.source &&
                          props.edition.utmParameters.source.length ? (
                              <div>utm_source = {props.edition.utmParameters.source}</div>
                          ) : undefined}
                          {props.edition.utmParameters.medium &&
                          props.edition.utmParameters.medium.length ? (
                              <div>utm_medium = {props.edition.utmParameters.medium}</div>
                          ) : undefined}
                          {getUtmCampaignParameters()}
                      </div>
                  )
                : undefined}
        </Stack>
    );
}

function renderToPreviewDetailItem(selection: SelectedRecipients): React.ReactElement {
    if (selection.recipientSelection && selection.recipientSelection.htmlUrlPath) {
        const {htmlUrlPath, name, recipientCount} = selection.recipientSelection;

        return (
            <Stack spacing={4}>
                <Title>To</Title>
                <Link variant='primary' href={htmlUrlPath} newTab={true}>
                    {name}
                </Link>
                {NutClientConfig.hasSmsAudiencesFF &&
                selection.type === 'audience' &&
                selection.recipientSelection.membershipStats ? (
                    <>
                        <div className={styles['recipient-count']}>
                            {selection.recipientSelection.membershipStats.totalCount.value.toLocaleString()}{' '}
                            {selection.recipientSelection.membershipStats.totalCount.value !== 1
                                ? 'members'
                                : 'member'}
                        </div>
                        <Card hasBorder={true} borderRadius={8}>
                            <CollapsibleEmailStats
                                stats={selection.recipientSelection.membershipStats}
                                isCollapsed={true}
                                showChevron={true}
                            />
                        </Card>
                    </>
                ) : (
                    <div className={styles['recipient-count']}>
                        {recipientCount.toLocaleString()}{' '}
                        {recipientCount !== 1 ? 'recipients' : 'recipient'}
                    </div>
                )}
            </Stack>
        );
    }

    return <div />;
}

/**
 * Generate a detail view for the preview. This consists of a simple title and
 * detail text. Its mostly pulled into this function so when I need to change
 * the style of these items I can do it all at once.
 *
 * @param {string} title The title of the detail item
 * @param {string} detail The detail text to show
 * @param {boolean} detailSecondaryMode If the detail text should be a lighter
 * color and italicized
 * @param {string} htmlUrl If the detail info is a link, this is the destination
 * @param {string} htmlUrlPath If the detail info is a link, this is the destination
 */
function renderPreviewDetailItem(
    title: string,
    detail: string | React.ReactNode
): React.ReactElement {
    return (
        <div>
            <Title>{title}</Title>
            <Body>{detail || <i>Nothing selected</i>}</Body>
        </div>
    );
}
